import React from 'react';
import { motion } from 'framer-motion';
import { StarIcon, CheckIcon, ShieldCheckIcon } from 'lucide-react';
import { trackEvent } from '../lib/metaPixel';
export function Hero() {
  return <section className="relative w-full flex items-center py-8 sm:py-10 lg:py-12 overflow-hidden" data-id="element-80">
      {/* Subtle background glow */}
      <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[600px] h-[600px] bg-teal/20 rounded-full blur-[120px] pointer-events-none" data-id="element-81" />

      <div className="max-w-6xl mx-auto px-6 w-full grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-10 lg:gap-16 items-start relative z-10" data-id="element-82">
        {/* LEFT: Product Mockup */}
        <motion.div initial={{
        opacity: 0,
        x: -30
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 1,
        ease: 'easeOut'
      }} className="flex justify-center lg:justify-end" data-id="element-83">
          <img src="https://cdn.magicpatterns.com/uploads/cVzzGFuBf4RNz63CWDdjgk/MOCKUT_DEL_LIBRO.png" alt="UNHOOKED book and tablet mockup — Break free from porn. Rewire your brain. Regain control." className="w-full max-w-lg lg:max-w-xl xl:max-w-2xl h-auto object-contain drop-shadow-2xl" data-id="element-84" />
        </motion.div>

        {/* RIGHT: Product Details */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 0.2,
        ease: 'easeOut'
      }} className="flex flex-col" data-id="element-85">
          <div className="flex items-center gap-3 mb-4" data-id="element-86">
            <div className="flex" data-id="element-87">
              {[...Array(4)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-current text-gold" data-id="element-88" />)}
              {/* 5th star: 90% gold, 10% ivory tip to represent 4.9 */}
              <div className="relative w-4 h-4" data-id="element-89">
                <StarIcon className="absolute inset-0 w-4 h-4 fill-current text-ivory" data-id="element-90" />
                <div className="absolute inset-0 overflow-hidden" style={{
                width: '90%'
              }} data-id="element-91">
                  <StarIcon className="w-4 h-4 fill-current text-gold" data-id="element-92" />
                </div>
              </div>
            </div>
            <span className="text-ivory/80 text-sm tracking-wider uppercase" data-id="element-93">
              (4.9) · 1,200+ Readers
            </span>
          </div>

          <h1 className="font-serif text-5xl sm:text-6xl lg:text-6xl xl:text-[60px] text-ivory mb-4 leading-[1.05] max-w-md" data-id="element-94">
            UNHOOKED
          </h1>

          <p className="text-lg sm:text-xl text-ivory/90 font-light mb-2 leading-snug" data-id="element-95">
            Rewire Your Brain. Regain Control.
          </p>

          <p className="text-sm sm:text-base text-ivory/55 font-light mb-9 leading-relaxed tracking-wide" data-id="element-96">
            A neuroscience-based approach to breaking compulsive porn use.
          </p>

          <ul className="space-y-2.5 mb-9" data-id="element-97">
            {['Understand the real mechanism behind compulsive porn use', 'Stop fighting urges with willpower', 'Restore focus, confidence, and mental clarity', 'Break the dopamine overstimulation cycle', 'Instant Private Access'].map((bullet, i) => <li key={i} className="flex items-start gap-3" data-id="element-98">
                <CheckIcon className="w-[18px] h-[18px] text-gold shrink-0 mt-0.5" data-id="element-99" />
                <span className="text-ivory/80 text-[15px] sm:text-[15.5px] leading-snug" data-id="element-100">
                  {bullet}
                </span>
              </li>)}
          </ul>

          <div className="mb-8" data-id="element-101">
            <div className="flex items-baseline gap-4 mb-4" data-id="element-102">
              <span className="text-4xl font-serif text-ivory" data-id="element-103">$14.99</span>
              <span className="text-xl text-ivory/40 line-through" data-id="element-104">$29.99</span>
            </div>

            <div className="flex flex-col sm:flex-row gap-4" data-id="element-105">
              <label className="flex-1 relative cursor-pointer" data-id="element-106">
                <input type="radio" name="format" className="peer sr-only" defaultChecked data-id="element-107" />
                <div className="p-4 border border-gold/40 bg-charcoal-light rounded-md peer-checked:border-gold peer-checked:bg-teal/10 transition-all" data-id="element-108">
                  <div className="text-ivory font-medium mb-1" data-id="element-109">Ebook Only</div>
                  <div className="text-ivory/60 text-sm" data-id="element-110">Instant Access</div>
                </div>
              </label>

              <label className="flex-1 relative cursor-not-allowed opacity-50" data-id="element-111">
                <input type="radio" name="format" className="peer sr-only" disabled data-id="element-112" />
                <div className="p-4 border border-charcoal-lighter bg-charcoal rounded-md" data-id="element-113">
                  <div className="text-ivory font-medium mb-1" data-id="element-114">
                    Ebook + Audiobook
                  </div>
                  <div className="text-gold/80 text-sm uppercase tracking-wider text-xs mt-1" data-id="element-115">
                    Coming Soon
                  </div>
                </div>
              </label>
            </div>
          </div>

          <a href="https://vitalyforms.com/cart/50274454110428:1" onClick={() => trackEvent('AddToCart', {
          content_name: 'UNHOOKED Ebook',
          content_ids: ['50274454110428'],
          content_type: 'product',
          value: 14.99,
          currency: 'USD'
        })} className="block w-full text-center bg-ivory text-charcoal hover:bg-white transition-all duration-300 py-5 rounded-md font-medium text-lg tracking-wide mb-4 shadow-glow-gold hover:scale-[1.02]" data-id="element-116">
            Start Reading Tonight
          </a>

          <div className="flex flex-col items-center gap-3" data-id="element-117">
            <div className="flex items-center gap-2 text-ivory/60 text-sm" data-id="element-118">
              <ShieldCheckIcon className="w-4 h-4" data-id="element-119" />
              <span data-id="element-120">30-Day Money-Back Guarantee</span>
            </div>
            <div className="text-xs tracking-[0.15em] uppercase text-ivory/40" data-id="element-121">
              Private Purchase • Instant Access • Read On Any Device
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}